import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

export default function ClientProfile() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');
  const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [activeTab, setActiveTab] = useState('overview');
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Status change
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusReason, setStatusReason] = useState('');

  const fetchProfile = () => {
    if (!companyId || !clientId) return;
    setLoading(true);
    fetch(`${API}/companies/${companyId}/clients/${clientId}`, { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => { setData(d); setEditForm(d.client || {}); })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchProfile(); }, [companyId, clientId, API]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const editableFields = ['name', 'organization_name', 'email', 'phone', 'website', 'industry',
        'registration_number', 'tax_number', 'address_line_1', 'address_line_2', 'city', 'state',
        'country', 'postal_code', 'notes', 'display_name', 'company_size', 'source', 'tags'];
      const payload = {};
      editableFields.forEach(f => { if (editForm[f] !== undefined) payload[f] = editForm[f]; });
      const res = await fetch(`${API}/companies/${companyId}/clients/${clientId}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();
      setEditing(false);
      fetchProfile();
    } catch { alert('Failed to update'); }
    finally { setSaving(false); }
  };

  const handleStatusChange = async () => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/clients/${clientId}/status`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus, reason: statusReason }),
      });
      if (!res.ok) throw new Error();
      setShowStatusModal(false);
      setStatusReason('');
      fetchProfile();
    } catch { alert('Failed to change status'); }
  };

  if (!companyId) return <div style={pageStyle}><p>Please select a company first.</p></div>;
  if (initialLoading) return <div style={pageStyle}><p>Loading...</p></div>;
  if (!data) return <div style={pageStyle}><p>Client not found.</p></div>;

  const client = data.client || {};
  const contacts = Array.isArray(client.contacts) ? client.contacts : [];
  const services = Array.isArray(client.services) ? client.services : [];
  const documents = Array.isArray(client.documents) ? client.documents : [];
  const notes = Array.isArray(client.client_notes) ? client.client_notes : [];
  const activities = Array.isArray(data.recent_activity) ? data.recent_activity : [];

  const healthColor = { good: '#22c55e', attention_needed: '#f59e0b', at_risk: '#ef4444' };
  const statusColor = { prospect: '#8b5cf6', active: '#10b981', on_hold: '#f59e0b', inactive: '#6b7280', archived: '#94a3b8' };

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'contacts', label: `Contacts (${client.contacts_count || 0})` },
    { key: 'services', label: `Services (${client.total_services_count || 0})` },
    { key: 'documents', label: `Documents (${client.documents_count || 0})` },
    { key: 'notes', label: 'Notes' },
    { key: 'activity', label: 'Activity' },
  ];

  return (
    <div style={pageStyle}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
        <button onClick={() => navigate('/company/clients/directory')} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', padding: '4px' }}>←</button>
        <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700 }}>{client.display_name || client.name}</h2>
        <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{client.client_code}</span>
        <span style={{ ...badgeStyle, background: (statusColor[client.status] || '#6b7280') + '20', color: statusColor[client.status] || '#6b7280' }}>
          {(client.status || '').replace(/_/g, ' ')}
        </span>
        <span style={{ ...badgeStyle, background: (healthColor[client.health] || '#6b7280') + '20', color: healthColor[client.health] || '#6b7280' }}>
          {(client.health || 'good').replace(/_/g, ' ')}
        </span>
      </div>

      {/* Quick Info */}
      <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', fontSize: '0.85rem', color: '#64748b', flexWrap: 'wrap' }}>
        {client.email && <span>📧 {client.email}</span>}
        {client.phone && <span>📞 {client.phone}</span>}
        {client.account_owner && <span>👤 Owner: {client.account_owner.name}</span>}
        {client.client_type && <span>📋 {client.client_type.name}</span>}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <button onClick={() => setEditing(!editing)} style={btnSecondary}>{editing ? 'Cancel Edit' : '✏️ Edit'}</button>
        <button onClick={() => { setShowStatusModal(true); setNewStatus(''); }} style={btnSecondary}>🔄 Change Status</button>
        <button onClick={() => navigate(`/company/clients/${clientId}/contacts`)} style={btnSecondary}>👤 Manage Contacts</button>
      </div>

      {/* Edit Form */}
      {editing && (
        <div style={{ ...cardStyle, padding: '20px', marginBottom: '20px' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>Edit Client Information</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '12px' }}>
            {[
              { key: 'name', label: 'Client Name' },
              { key: 'organization_name', label: 'Organization' },
              { key: 'email', label: 'Email' },
              { key: 'phone', label: 'Phone' },
              { key: 'website', label: 'Website' },
              { key: 'industry', label: 'Industry' },
              { key: 'registration_number', label: 'Registration No.' },
              { key: 'tax_number', label: 'Tax No.' },
              { key: 'address_line_1', label: 'Address' },
              { key: 'city', label: 'City' },
              { key: 'state', label: 'State' },
              { key: 'country', label: 'Country' },
              { key: 'postal_code', label: 'Postal Code' },
            ].map(f => (
              <div key={f.key}>
                <label style={labelStyle}>{f.label}</label>
                <input
                  value={editForm[f.key] || ''}
                  onChange={e => setEditForm({ ...editForm, [f.key]: e.target.value })}
                  style={formInputStyle}
                />
              </div>
            ))}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Notes</label>
              <textarea
                value={editForm.notes || ''}
                onChange={e => setEditForm({ ...editForm, notes: e.target.value })}
                style={{ ...formInputStyle, minHeight: '80px', resize: 'vertical' }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px', marginTop: '16px', justifyContent: 'flex-end' }}>
            <button onClick={() => setEditing(false)} style={btnCancel}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={btnPrimary}>{saving ? 'Saving...' : 'Save Changes'}</button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0', borderBottom: '2px solid #e2e8f0', marginBottom: '20px' }}>
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            style={{
              padding: '10px 16px', border: 'none', background: 'none', cursor: 'pointer',
              fontSize: '0.85rem', fontWeight: activeTab === t.key ? 700 : 500,
              color: activeTab === t.key ? '#3b82f6' : '#64748b',
              borderBottom: activeTab === t.key ? '2px solid #3b82f6' : '2px solid transparent',
              marginBottom: '-2px',
            }}
          >{t.label}</button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
          {/* Client Info */}
          <div style={{ ...cardStyle, padding: '16px' }}>
            <h4 style={sectionTitle}>Client Information</h4>
            {[
              ['Organization', client.organization_name],
              ['Email', client.email],
              ['Phone', client.phone],
              ['Website', client.website],
              ['Industry', client.industry],
              ['Registration No.', client.registration_number],
              ['Tax No.', client.tax_number],
              ['Source', client.source],
              ['First Active', client.first_active_date],
              ['Last Activity', client.last_activity_at ? new Date(client.last_activity_at).toLocaleDateString() : null],
            ].map(([label, val]) => val && (
              <div key={label} style={infoRow}>
                <span style={infoLabel}>{label}</span>
                <span style={infoValue}>{val}</span>
              </div>
            ))}
          </div>

          {/* Address */}
          <div style={{ ...cardStyle, padding: '16px' }}>
            <h4 style={sectionTitle}>Address</h4>
            {[
              ['Address', [client.address_line_1, client.address_line_2].filter(Boolean).join(', ')],
              ['City', client.city],
              ['State', client.state],
              ['Country', client.country],
              ['Postal Code', client.postal_code],
            ].map(([label, val]) => val && (
              <div key={label} style={infoRow}>
                <span style={infoLabel}>{label}</span>
                <span style={infoValue}>{val}</span>
              </div>
            ))}
          </div>

          {/* Company Relationships */}
          {Array.isArray(client.company_relationships) && client.company_relationships.length > 0 && (
            <div style={{ ...cardStyle, padding: '16px' }}>
              <h4 style={sectionTitle}>Company Relationships</h4>
              {client.company_relationships.map((r, i) => (
                <div key={i} style={{ ...infoRow, flexDirection: 'column', gap: '2px' }}>
                  <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{r.company?.name || 'Company'}</span>
                  <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                    Status: {r.status} {r.is_primary ? '(Primary)' : ''} • Owner: {r.account_owner?.name || '—'}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Notes Preview */}
          {client.notes && (
            <div style={{ ...cardStyle, padding: '16px' }}>
              <h4 style={sectionTitle}>Notes</h4>
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#475569', whiteSpace: 'pre-wrap' }}>{client.notes}</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'contacts' && (
        <div style={{ ...cardStyle, overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Role</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Phone</th>
                <th style={thStyle}>Position</th>
                <th style={thStyle}>Primary</th>
              </tr>
            </thead>
            <tbody>
              {contacts.length === 0 ? (
                <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No contacts</td></tr>
              ) : contacts.map(c => (
                <tr key={c.id}>
                  <td style={tdStyle}><span style={{ fontWeight: 600 }}>{c.first_name} {c.last_name || ''}</span></td>
                  <td style={tdStyle}>{c.contact_role?.name || '—'}</td>
                  <td style={tdStyle}>{c.email || '—'}</td>
                  <td style={tdStyle}>{c.phone || '—'}</td>
                  <td style={tdStyle}>{c.position || '—'}</td>
                  <td style={tdStyle}>{c.is_primary ? '✅' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'services' && (
        <div style={{ ...cardStyle, overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Service</th>
                <th style={thStyle}>Category</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Start Date</th>
                <th style={thStyle}>End Date</th>
                <th style={thStyle}>Service Owner</th>
              </tr>
            </thead>
            <tbody>
              {services.length === 0 ? (
                <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No services</td></tr>
              ) : services.map(s => (
                <tr key={s.id}>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 600 }}>{s.service_name}</div>
                    {s.service_code && <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{s.service_code}</div>}
                  </td>
                  <td style={tdStyle}>{s.service_category || '—'}</td>
                  <td style={tdStyle}>
                    <span style={{ ...badgeStyle, background: s.status === 'active' ? '#10b98120' : '#6b728020', color: s.status === 'active' ? '#10b981' : '#6b7280' }}>
                      {s.status}
                    </span>
                  </td>
                  <td style={tdStyle}>{(s.service_type || '').replace(/_/g, ' ')}</td>
                  <td style={tdStyle}>{s.start_date || '—'}</td>
                  <td style={tdStyle}>{s.expected_end_date || s.actual_end_date || '—'}</td>
                  <td style={tdStyle}>{s.service_owner?.name || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'documents' && (
        <div style={{ ...cardStyle, overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Document</th>
                <th style={thStyle}>Category</th>
                <th style={thStyle}>Expiry</th>
                <th style={thStyle}>Uploaded By</th>
                <th style={thStyle}>Date</th>
              </tr>
            </thead>
            <tbody>
              {documents.length === 0 ? (
                <tr><td colSpan={5} style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No documents</td></tr>
              ) : documents.map(d => (
                <tr key={d.id}>
                  <td style={tdStyle}>{d.name}</td>
                  <td style={tdStyle}>{d.document_category?.name || '—'}</td>
                  <td style={tdStyle}>{d.expiry_date || '—'}</td>
                  <td style={tdStyle}>{d.uploaded_by_user?.name || '—'}</td>
                  <td style={tdStyle}>{new Date(d.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'notes' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {notes.length === 0 ? (
            <div style={{ ...cardStyle, padding: '20px', textAlign: 'center', color: '#94a3b8' }}>No notes</div>
          ) : notes.map(n => (
            <div key={n.id} style={{ ...cardStyle, padding: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {n.note_type?.name && <span style={{ ...badgeStyle, background: '#e0e7ff', color: '#4f46e5' }}>{n.note_type.name}</span>}
                  {n.importance === 'important' && <span style={{ ...badgeStyle, background: '#fef3c7', color: '#d97706' }}>Important</span>}
                </div>
                <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                  {n.created_by_user?.name || 'System'} • {new Date(n.created_at).toLocaleString()}
                </span>
              </div>
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#334155', whiteSpace: 'pre-wrap' }}>{n.content}</p>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'activity' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {activities.length === 0 ? (
            <div style={{ ...cardStyle, padding: '20px', textAlign: 'center', color: '#94a3b8' }}>No activity</div>
          ) : activities.map((a, i) => (
            <div key={i} style={{ display: 'flex', gap: '12px', padding: '10px 0', borderBottom: '1px solid #f1f5f9', alignItems: 'flex-start' }}>
              <div style={{
                width: '10px', height: '10px', borderRadius: '50%', marginTop: '4px', flexShrink: 0,
                background: a.importance === 'critical' ? '#ef4444' : a.importance === 'important' ? '#f59e0b' : a.importance === 'warning' ? '#f97316' : '#3b82f6',
              }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.85rem', color: '#334155', fontWeight: 500 }}>{a.description}</div>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '2px' }}>
                  {a.activity_type?.replace(/_/g, ' ')} • {a.user?.name || 'System'} • {new Date(a.created_at).toLocaleString()}
                  {a.source_module && ` • ${a.source_module.replace(/_/g, ' ')}`}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Status Change Modal */}
      {showStatusModal && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <h3 style={{ margin: '0 0 16px', fontSize: '1.1rem', fontWeight: 700 }}>Change Client Status</h3>
            <p style={{ fontSize: '0.85rem', color: '#64748b', margin: '0 0 12px' }}>Current: <strong>{client.status}</strong></p>
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>New Status</label>
              <select value={newStatus} onChange={e => setNewStatus(e.target.value)} style={formInputStyle}>
                <option value="">Select...</option>
                {['prospect', 'active', 'on_hold', 'inactive', 'archived']
                  .filter(s => s !== client.status)
                  .map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>Reason</label>
              <textarea value={statusReason} onChange={e => setStatusReason(e.target.value)} style={{ ...formInputStyle, minHeight: '60px' }} placeholder="Reason for status change..." />
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowStatusModal(false)} style={btnCancel}>Cancel</button>
              <button onClick={handleStatusChange} disabled={!newStatus} style={btnPrimary}>Change Status</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const pageStyle = { padding: '24px', maxWidth: '1200px' };
const cardStyle = { background: '#fff', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' };
const thStyle = { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e2e8f0', color: '#64748b', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' };
const tdStyle = { padding: '10px 12px', borderBottom: '1px solid #f1f5f9', color: '#334155' };
const badgeStyle = { padding: '2px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600, textTransform: 'capitalize' };
const sectionTitle = { margin: '0 0 12px', fontSize: '0.9rem', fontWeight: 600, color: '#1e293b' };
const infoRow = { display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f8fafc' };
const infoLabel = { fontSize: '0.8rem', color: '#94a3b8' };
const infoValue = { fontSize: '0.85rem', color: '#334155', fontWeight: 500 };
const labelStyle = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '4px' };
const formInputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.85rem', boxSizing: 'border-box' };
const btnPrimary = { padding: '8px 16px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' };
const btnSecondary = { padding: '6px 14px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', fontSize: '0.85rem', cursor: 'pointer' };
const btnCancel = { padding: '8px 16px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', fontSize: '0.85rem', cursor: 'pointer' };
const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalStyle = { background: '#fff', borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '450px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' };
